import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  FileText,
  History,
} from 'lucide-react'
import { Dialog, Toast, ToastType } from '../../../components/ui'
import { apiService } from '../../../services/api'

interface PublicationLog {
  id: string
  action: string
  message?: string
  errorDetails?: any
  attemptNumber?: number
  externalData?: any
  createdBy?: string
  createdAt: string
}

interface VehiclePublication {
  publicationId: string
  platform: string
  status: string
  externalId?: string
  externalUrl?: string
  errorMessage?: string
  publishedAt?: string
  createdAt: string
  updatedAt: string
  logs: PublicationLog[]
}

interface VehiclePublicationLogsProps {
  isOpen: boolean
  onClose: () => void
  vehicleId: string
  vehicleName: string
}

export const VehiclePublicationLogs: React.FC<VehiclePublicationLogsProps> = ({
  isOpen,
  onClose,
  vehicleId,
  vehicleName,
}) => {
  const { t } = useTranslation()
  const [publications, setPublications] = useState<VehiclePublication[]>([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (isOpen && vehicleId) {
      loadLogs()
    }
  }, [isOpen, vehicleId])

  const loadLogs = async () => {
    try {
      setLoading(true)
      const data = await apiService.get<VehiclePublication[]>(
        `/integrations/publications/logs/${vehicleId}`
      )
      setPublications(data)
    } catch (err: any) {
      setToast({
        message: err.message || t('marketplace.logs.error'),
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const getActionIcon = (action: string) => {
    switch (action.toUpperCase()) {
      case 'CREATED':
        return <FileText className="w-4 h-4 text-blue-600" />
      case 'QUEUED':
        return <Clock className="w-4 h-4 text-yellow-600" />
      case 'PROCESSING':
        return <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
      case 'PUBLISHED':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'UPDATED':
        return <CheckCircle className="w-4 h-4 text-blue-600" />
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-600" />
      case 'RETRYING':
        return <Clock className="w-4 h-4 text-orange-600" />
      case 'REMOVED':
        return <XCircle className="w-4 h-4 text-gray-600" />
      case 'ERROR':
        return <AlertTriangle className="w-4 h-4 text-red-600" />
      default:
        return <History className="w-4 h-4 text-gray-600" />
    }
  }

  const getActionColor = (action: string) => {
    switch (action.toUpperCase()) {
      case 'CREATED':
      case 'UPDATED':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
      case 'QUEUED':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
      case 'PUBLISHED':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
      case 'FAILED':
      case 'ERROR':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      default:
        return 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR')
  }

  const toggleLogDetails = (logId: string) => {
    const newExpanded = new Set(expandedLogs)
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId)
    } else {
      newExpanded.add(logId)
    }
    setExpandedLogs(newExpanded)
  }

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <Dialog isOpen={isOpen} onClose={onClose} title={`${t('marketplace.logs.title')} - ${vehicleName}`}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : publications.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t('marketplace.logs.noPublications')}</p>
          </div>
        ) : (
          <div className="space-y-6 max-h-[600px] overflow-y-auto">
            {publications.map((publication) => (
              <div
                key={publication.publicationId}
                className="border border-border rounded-lg p-4"
              >
                {/* Publication Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-foreground">
                      {publication.platform}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Status: {publication.status}
                    </p>
                  </div>
                  {publication.externalUrl && (
                    <a
                      href={publication.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      {t('marketplace.logs.viewAd')}
                    </a>
                  )}
                </div>

                {publication.errorMessage && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                    <p className="text-sm text-red-800 dark:text-red-200">
                      <strong>Erro:</strong> {publication.errorMessage}
                    </p>
                  </div>
                )}

                {/* Timeline of Logs */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    {t('marketplace.logs.eventHistory')}
                  </h4>

                  {publication.logs.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">
                      {t('marketplace.logs.noEvents')}
                    </p>
                  ) : (
                    <div className="relative">
                      {/* Timeline Line */}
                      <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

                      {publication.logs.map((log, index) => (
                        <div key={log.id} className="relative pl-10 pb-4 last:pb-0">
                          {/* Timeline Dot */}
                          <div className="absolute left-0 top-0 flex items-center justify-center w-8 h-8 rounded-full bg-background border-2 border-border">
                            {getActionIcon(log.action)}
                          </div>

                          {/* Log Content */}
                          <div
                            className={`p-3 rounded-lg border ${getActionColor(log.action)}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-sm">
                                    {t(`marketplace.logs.actions.${log.action}` as any) || log.action}
                                  </span>
                                  {log.attemptNumber && (
                                    <span className="text-xs px-2 py-0.5 bg-background rounded-full border border-border">
                                      {t('marketplace.logs.attempt')} #{log.attemptNumber}
                                    </span>
                                  )}
                                </div>

                                {log.message && (
                                  <p className="text-sm text-foreground mb-2">
                                    {log.message}
                                  </p>
                                )}

                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{formatDate(log.createdAt)}</span>
                                  {log.createdBy && <span>• {log.createdBy}</span>}
                                </div>

                                {/* Collapsible Details */}
                                {(log.errorDetails || log.externalData) && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => toggleLogDetails(log.id)}
                                      className="text-xs text-primary hover:underline"
                                    >
                                      {expandedLogs.has(log.id)
                                        ? t('marketplace.logs.hideDetails')
                                        : t('marketplace.logs.showDetails')}
                                    </button>

                                    {expandedLogs.has(log.id) && (
                                      <div className="mt-2 p-2 bg-background rounded border border-border">
                                        {log.errorDetails && (
                                          <div className="mb-2">
                                            <p className="text-xs font-medium mb-1">
                                              {t('marketplace.logs.errorDetails')}
                                            </p>
                                            <pre className="text-xs overflow-auto max-h-32 bg-muted p-2 rounded">
                                              {JSON.stringify(log.errorDetails, null, 2)}
                                            </pre>
                                          </div>
                                        )}

                                        {log.externalData && (
                                          <div>
                                            <p className="text-xs font-medium mb-1">
                                              {t('marketplace.logs.apiResponse')}
                                            </p>
                                            <pre className="text-xs overflow-auto max-h-32 bg-muted p-2 rounded">
                                              {JSON.stringify(log.externalData, null, 2)}
                                            </pre>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Dialog>
    </>
  )
}
